import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Button, Box, TextField,CircularProgress, Checkbox, FormGroup, Skeleton } from '@mui/material';
import Logo from '../../images/logo/Logo.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_CONFIG } from "../../api/apiUrl";
import toast, { Toaster } from 'react-hot-toast';

interface PlotServiceReq {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  isDeleted: boolean;
}

const PlotRequirements = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showOtherText, setShowOtherText] = useState(false);
  const [plotFacing, setPlotFacing] = useState('');
  const [serviceRequirements, setServiceRequirements] = useState<PlotServiceReq[]>([]);
  const [selectedRequirements, setSelectedRequirements] = useState<number[]>([]);
  const [otherText, setOtherText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { selectedService, initialData } = location.state || {};

  useEffect(() => {
    async function getRequirements() {
      setIsDataLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('id_token');
        const contractorTypesId = selectedService?.id;
        const url = `${API_CONFIG.PLOT_SERVICE_REQ}?contractorTypesId.equals=${contractorTypesId}&page=0&size=105`;
        const response = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        setServiceRequirements(response.data);
      } catch (error: any) {
        setError('Failed to load service requirements');
      } finally {
        setIsDataLoading(false);
      }
    }
    getRequirements();
  }, [selectedService]);

  function handleRequirementChange(id: number) {
    return (e:any) => {
      if (e.target.checked) {
        setSelectedRequirements(prev => [...prev, id]);
      } else {
        setSelectedRequirements(prev => prev.filter(reqId => reqId !== id));
      }
    };
  }

  function handlePlotFacingChange(e:any) {
    setPlotFacing(e.target.value);
  }

  function handleOtherTextChange(e:any) {
    setOtherText(e.target.value);
  }

async function handleSubmit(e: any) {
  e.preventDefault();
  setIsLoading(true);
  setError(null);
  try {
    const token = localStorage.getItem('id_token');
    let remarks = '';
    if (showOtherText && otherText) {
      remarks = otherText;
    }
    const updatedData = {
      plot: {
        ...initialData?.plot,
        plotFacing,
        remarks: remarks || 'No additional remarks',
        contractorTypes: initialData?.plot?.contractorTypes || null,
      },
      files: initialData?.files || [],
      plotServices: [...(initialData?.plotServices || []), ...selectedRequirements],
    };
    const response = await axios.post(API_CONFIG.PLOTS, updatedData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (response.status === 200 || response.status === 201) {
      toast.success('Plot created successfully!');
      // Pass selectedService and plotId to the /booking route
      navigate('/booking', {
        state: { selectedService, plotId: response.data.plot.id }, // Updated navigation
      });
    }
  } catch (error: any) {
    setError(error.response?.data?.detail || 'Error submitting data');
    toast.error(error.response?.data?.detail || 'Failed to create plot');
  } finally {
    setIsLoading(false);
  }
}

  const isButtonDisabled =
    !plotFacing || (selectedRequirements.length === 0 && (!showOtherText || !otherText)) || isLoading;

  return (
    <Box sx={styles.mainContainer}>
      <Toaster position="top-right" />
      <Card sx={styles.card}>
        <CardContent>
          <Box sx={styles.logoContainer}><img src={Logo} alt="Logo" style={styles.logo} /></Box>

          {isDataLoading ? (
            <Box sx={{ my: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 2, borderRadius: '50px' }} />
              <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 2, borderRadius: '50px' }} />
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: '5px' }} />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
                  {error}
                </Typography>
              )}
              <Box>
                <Box>
                  <Typography sx={{ ...styles.inputLabel, mt: 2 }}>Mention Facing Direction of Plot/House.</Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter Facing Direction"
                    variant="outlined"
                    size="small"
                    sx={styles.textField}
                    value={plotFacing}
                    onChange={handlePlotFacingChange}
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: '15px', mb: 1, fontWeight: 400 }}>Requirements</Typography>
                  <FormGroup>
                    {serviceRequirements.map((requirement) => (
                      <Box key={requirement.id} sx={{ gap: 1, display: 'flex', alignItems: 'center', margin: '-3px' }}>
                        <Checkbox
                          sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
                          checked={selectedRequirements.includes(requirement.id)}
                          onChange={handleRequirementChange(requirement.id)}
                        />
                        <Typography sx={{ fontSize: '15px', color: '#212121', userSelect: 'none', pointerEvents: 'none', ml: '-3px' }}>
                          {requirement.name}
                        </Typography>
                      </Box>
                    ))}
                  </FormGroup>
                </Box>

                <Box>
                  <Box sx={{ gap: 1, display: 'flex', alignItems: 'center', margin: '-3px' }}>
                    <Checkbox
                      sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
                      checked={showOtherText}
                      onChange={(e) => setShowOtherText(e.target.checked)}
                    />
                    <Typography sx={{ fontSize: '15px', color: '#212121', userSelect: 'none', pointerEvents: 'none', ml: '-5px' }}>
                      Other
                    </Typography>
                  </Box>
                  {showOtherText && (
                    <TextField
                      placeholder="Enter here"
                      variant="outlined"
                      minRows={9}
                      fullWidth
                      sx={{ ...styles.textFieldTextArea, mt: 2 }}
                      value={otherText}
                      onChange={handleOtherTextChange}
                    />
                  )}
                </Box>

                <Box sx={styles.submitContainer}>
                  <Button type="submit" sx={styles.submitButton} disabled={isButtonDisabled}>
                     {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const styles = {
  mainContainer: { my: { xs: 5, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 0, md: 2 } },
  card: { boxShadow: '0px 0px 4px 0px #00000033', maxWidth: 500, width: '90%', p: { xs: 1, md: 2 }, borderRadius: '20px' },
  logoContainer: { display: 'flex', justifyContent: 'center', mb: 3 },
  logo: { width: '130px' },
  textField: { '& .MuiOutlinedInput-root': { borderRadius: '50px', borderColor: '#89652A', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' } } },
  textFieldTextArea: { '& .MuiOutlinedInput-root': { borderRadius: '5px', borderColor: '#89652A', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' } } },
  inputLabel: { fontSize: '15px', fontWeight: 400, mb: 1.5, color: '#212121' },
  submitContainer: { display: 'flex', justifyContent: 'center', mt: 3 },
  submitButton: { textTransform: 'capitalize', fontSize: { xs: '16px', md: '16px' }, fontWeight: 600, borderRadius: '28px', color: 'white', bgcolor: '#89652A', py: 0.7, px: { xs: 4, md: 6 }, '&:hover': { bgcolor: '#6b4e20' }, '&:disabled': { bgcolor: '#a88b5e' } },
};

export default PlotRequirements;